import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Form, FormControl, Row, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { ADMIN_ROLE, DEFAULT_ORDER_STATUS, PROCESSOR_ROLE } from '../../../../helpers/constants';
import { canViewCollectedFees, isClient } from '../../../../helpers/global';
import { onScrollToErrorPosition } from '../../../../helpers/ui';
import useAppChoices from '../../../../hooks/useAppChoices';
import AssignedProcessorsMultiSelectModal from './Common/AssignedProcessorsMultiSelect';
import AttachSupportingDocuments, {
  getPersistedSupportingDocuments
} from '../../../newOrder/Components/AttachSupportingDocuments';

const initialFormFieldError = [
  { key: 'status', isNotValid: false, type: 'string' },
  { key: 'assignedProcessors', isNotValid: false, type: 'array' },
  { key: 'hardCopy', isNotValid: false, type: 'string' },
  { key: 'titleCertification', isNotValid: false, type: 'string' },
  { key: 'buyerCertification', isNotValid: false, type: 'string' },
  { key: 'lenderCertification', isNotValid: false, type: 'string' },
  { key: 'underwriterCertification', isNotValid: false, type: 'object' }
];

const HARD_COST_REQUIRED_STATUSES = ['Information Requested', 'Ready for QA', 'Information Received'];

// function to update whether the fields are valid or not
const checkIsValid = (formData, fieldError = []) => {
  let error = false;
  return {
    result: fieldError.map(field => {
      const key = field.key;
      const value = formData[key];
      const type = field.type;

      if (type === 'string') {
        if (value === '' || value === null || value === undefined) {
          error = true;
          return { key, isNotValid: true, type };
        } else {
          return { key, isNotValid: false, type };
        }
      } else if (type === 'array') {
        if (value.length <= 0) {
          error = true;
          return { key, isNotValid: true, type };
        } else {
          return { key, isNotValid: false, type };
        }
      } else if (type === 'object') {
        if (value.value === '' || value.value === null || value.value === undefined) {
          error = true;
          return { key, isNotValid: true, type };
        } else if (value.value === 'Others') {
          if (value.other === '' || value.other === null || value.other === undefined) {
            error = true;
            return { key, isNotValid: true, type };
          } else {
            return { key, isNotValid: false, type };
          }
        } else {
          return { key, isNotValid: false, type };
        }
      }
    }),
    error
  };
};

// function to update the field on change
const updateOnChangeErrorField = (fieldError = [], key) => {
  return fieldError.map(field => {
    if (field.key === key) {
      return { key, isNotValid: false, type: field.type };
    } else {
      return field;
    }
  });
};

const SurveyOrderForm = ({ onSubmit, isEditingOrder, loading, serviceCreation = false, isPublic, folderId = null }) => {
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const currentUserInfo = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const canSeeCollectedFees = useMemo(() => canViewCollectedFees(currentUserInfo), [currentUserInfo]);
  const [showAssignedProcessors, setShowAssignedProcesser] = useState(null);
  const [formFieldError, setFormFieldError] = useState(
    !isClient(role) && !isPublic
      ? initialFormFieldError
      : initialFormFieldError.filter(v => v.key !== 'status' && v.key !== 'assignedProcessors')
  );
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [supportingDocuments, setSupportingDocuments] = useState([]);
  const surveyStatuses = useAppChoices('Survey Status');
  const surveyor = useAppChoices('Surveyor');
  const underwriterCertification = [...useAppChoices('Underwriter Certification'), 'Others'];

  const [formData, setFormData] = useState(
    !isClient(role) && !isPublic
      ? {
          status: DEFAULT_ORDER_STATUS,
          assignedProcessors: [],
          assignedSurveyor: '',
          hardCost: '',
          invoiceAmount: '',
          estimatedTurnAroundTime: '',
          listingAgentName: '',
          listingAgentPhone: '',
          hardCopy: 'yes',
          titleCertification: '',
          buyerCertification: '',
          lenderCertification: '',
          underwriterCertification: { value: underwriterCertification[0] || '', other: '' },
          rush: false,
          neededDate: '',
          files: [],
          processingFee: isEditingOrder?.processingFee
        }
      : {
          hardCopy: 'yes',
          titleCertification: '',
          buyerCertification: '',
          lenderCertification: '',
          underwriterCertification: { value: underwriterCertification[0] || '', other: '' },
          rush: false,
          neededDate: '',
          processingFee: isEditingOrder?.processingFee,
          assignedSurveyor: ''
        }
  );

  useEffect(() => {
    if (isEditingOrder?._id) {
      setFormData({
        status: isEditingOrder?.status || DEFAULT_ORDER_STATUS,
        assignedProcessors: isEditingOrder?.assignedProcessors,
        assignedSurveyor: isEditingOrder?.assignedSurveyor || '',
        hardCost: isEditingOrder?.hardCost || '',
        invoiceAmount: isEditingOrder?.invoiceAmount || '',
        estimatedTurnAroundTime: isEditingOrder?.estimatedTurnAroundTime
          ? moment(isEditingOrder?.estimatedTurnAroundTime).format('YYYY-MM-DD')
          : '',
        listingAgentName: isEditingOrder?.listingAgentName || '',
        listingAgentPhone: isEditingOrder?.listingAgentPhone || '',
        hardCopy: isEditingOrder?.hardCopy ? 'yes' : 'no',
        titleCertification: isEditingOrder?.titleCertification || '',
        buyerCertification: isEditingOrder?.buyerCertification || '',
        lenderCertification: isEditingOrder?.lenderCertification || '',
        underwriterCertification: isEditingOrder?.underwriterCertification
          ? {
              value: underwriterCertification.find(d => d === isEditingOrder?.underwriterCertification)
                ? isEditingOrder?.underwriterCertification
                : 'Others',
              other: underwriterCertification.find(d => d === isEditingOrder?.underwriterCertification)
                ? ''
                : isEditingOrder?.underwriterCertification
            }
          : { value: '', other: '' },
        processingFee: isEditingOrder?.processingFee || '',
        neededDate: isEditingOrder?.neededDate ? moment(isEditingOrder?.neededDate).format('YYYY-MM-DD') : '',
        rush: isEditingOrder?.rush || false,
        files: []
      });
    }
  }, [isEditingOrder?._id]);

  const collectedFeesDisplay = (parseFloat(formData.invoiceAmount) || 0) - (parseFloat(formData.hardCost) || 0);

  const isHardCostEmpty = formData.hardCost === '' || formData.hardCost === null || formData.hardCost === undefined;

  const isHardCostInvalid = HARD_COST_REQUIRED_STATUSES.includes(formData.status) && isHardCostEmpty;

  const hasSupportingDocumentUploadIssues = supportingDocuments.some(
    doc => doc.status === 'pending' || doc.status === 'error'
  );

  const onHandleClickSubmit = () => {
    if (uploadedFiles.some(f => f.status === 'ERROR')) {
      return toast.error('One or more files have failed uploading, please discard these files and try again!');
    }
    if (supportingDocuments.some(doc => doc.status === 'error')) {
      return toast.error('One or more supporting documents failed to upload. Please remove them and try again.');
    }
    if (supportingDocuments.some(doc => doc.status === 'pending')) {
      return toast.warning('Please wait for supporting documents to finish uploading.');
    }
    const { result, error } = checkIsValid(formData, formFieldError);
    if (error) {
      setFormFieldError(result);
      const fieldId = result.find(e => e.isNotValid === true);
      onScrollToErrorPosition(fieldId.key);
    } else if (isHardCostInvalid) {
      onScrollToErrorPosition('hardCost');
    } else {
      onSubmit({
        ...formData,
        underwriterCertification: formData.underwriterCertification?.other || formData.underwriterCertification?.value,
        hardCopy: formData.hardCopy === 'yes',
        assignedProcessors: !isClient(role) && !isPublic ? formData.assignedProcessors.map(a => a._id) : undefined,
        ...(!isEditingOrder?._id && getPersistedSupportingDocuments(supportingDocuments).length
          ? { supportingDocuments: getPersistedSupportingDocuments(supportingDocuments) }
          : {})
      });
    }
  };

  const onFormDataChange = (field, e) => {
    if (e && e?.target) {
      const updatedFormData = { ...formData };
      updatedFormData[field] = e.target.value;
      setFormData(updatedFormData);
      setFormFieldError(updateOnChangeErrorField(formFieldError, field));
    }
  };

  const handleUnderwriterCertification = (key, e) => {
    if (e && e?.target) {
      const updatedFormData = { ...formData };
      updatedFormData.underwriterCertification[key] = e.target.value;
      if (key === 'value') {
        updatedFormData.underwriterCertification['other'] = '';
      }
      setFormData(updatedFormData);
      setFormFieldError(updateOnChangeErrorField(formFieldError, 'underwriterCertification'));
    }
  };

  const onHandleAssignedProcessors = value => {
    setFormData(prevData => ({ ...prevData, assignedProcessors: value }));
    setFormFieldError(updateOnChangeErrorField(formFieldError, 'assignedProcessors'));
  };

  const isFieldNotValid = key => {
    const field = formFieldError.find(f => f.key === key);
    return field?.isNotValid;
  };

  return (
    <Container fluid>
      {!isClient(role) && !isPublic && (
        <AssignedProcessorsMultiSelectModal
          show={showAssignedProcessors}
          onHide={() => setShowAssignedProcesser(false)}
          onConfirm={onHandleAssignedProcessors}
          prevData={formData.assignedProcessors}
          isEditing={isEditingOrder?._id}
        />
      )}

      {!isClient(role) && !isPublic && (
        <Row className="align-items-end">
          <Col md={4} className="p-0">
            <Form.Group className="midFont rounded">
              <Form.Label className="font-weight-bold">
                Status<span className="text-danger">*</span>
              </Form.Label>
              <select
                onChange={e => onFormDataChange('status', e)}
                className="form-control form-control-sm"
                value={formData.status}
              >
                {surveyStatuses.map((status, index) => (
                  <option key={index} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="midFont">
              <Form.Label>
                Assigned Processor<span className="text-danger">*</span>
              </Form.Label>
              <input
                id="assignedProcessors"
                type="text"
                className={`form-control form-control-sm ${isFieldNotValid('assignedProcessors') && 'input-invalid'}`}
                onClick={() => setShowAssignedProcesser(true)}
                placeholder="select"
                value={
                  formData.assignedProcessors.length > 0
                    ? formData.assignedProcessors.length > 1
                      ? `${formData.assignedProcessors[0]?.name} and ${formData.assignedProcessors.length - 1} others`
                      : formData.assignedProcessors[0]?.name
                    : ''
                }
              />
              {isFieldNotValid('assignedProcessors') && (
                <p className="xsmallFont text-rush">This is a required Field</p>
              )}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="midFont">
              <Form.Label>Assigned Surveyor</Form.Label>
              <select
                id="assignedSurveyor"
                onChange={e => onFormDataChange('assignedSurveyor', e)}
                className="form-control form-control-sm"
                value={formData.assignedSurveyor}
              >
                <option value=""></option>
                {surveyor.map((order, index) => (
                  <option key={index} value={order}>
                    {order}
                  </option>
                ))}
              </select>
            </Form.Group>
          </Col>
        </Row>
      )}
      {!isClient(role) && !isPublic && (
        <Row>
          <Col md={4}>
            <Form.Group className="midFont">
              <Form.Label>Estimated Turnaround Time</Form.Label>
              <input
                type="date"
                onChange={e => onFormDataChange('estimatedTurnAroundTime', e)}
                className="form-control form-control-sm"
                value={formData.estimatedTurnAroundTime}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="midFont">
              <Form.Label>Hard Cost{isHardCostInvalid && <span className="text-danger">*</span>}</Form.Label>
              <input
                id="hardCost"
                onChange={e => onFormDataChange('hardCost', e)}
                type="number"
                value={formData.hardCost}
                className={`form-control form-control-sm ${isHardCostInvalid ? 'input-invalid' : ''}`}
              />
              {isHardCostInvalid && <p className="xsmallFont text-rush mb-0 mt-1">This is a required Field</p>}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="midFont">
              <Form.Label>Processing Fee</Form.Label>
              <input
                id="processingFee"
                onChange={e => onFormDataChange('processingFee', e)}
                type="number"
                value={formData.processingFee}
                className="form-control form-control-sm"
              />
            </Form.Group>
          </Col>
        </Row>
      )}
      {!isClient(role) && !isPublic && (
        <Row>
          <Col md={4}>
            <Form.Group className="midFont">
              <Form.Label>Invoice Amount</Form.Label>
              <input
                id="invoiceAmount"
                onChange={e => onFormDataChange('invoiceAmount', e)}
                type="number"
                value={formData.invoiceAmount}
                className="form-control form-control-sm"
              />
            </Form.Group>
          </Col>
          {canSeeCollectedFees && isEditingOrder?._id && (
            <Col md={4} className="midFont text-muted">
              <span className="text-dark d-block">Collected fees</span>
              <span className="text-body">
                {!Number.isNaN(collectedFeesDisplay) ? `$${Number(collectedFeesDisplay).toFixed(2)}` : 'N/A'}
              </span>
              <div className="xsmallFont text-muted">
                Total invoice amount: ${(parseFloat(formData.invoiceAmount) || 0).toFixed(2)}
              </div>
            </Col>
          )}
        </Row>
      )}
      <Row>
        <Col>
          <Form.Group className="midFont">
            <Form.Label>Listing Agent Name</Form.Label>
            <input
              onChange={e => onFormDataChange('listingAgentName', e)}
              type="text"
              value={formData.listingAgentName}
              className="form-control form-control-sm"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label>Listing Agent Phone</Form.Label>
            <input
              onChange={e => onFormDataChange('listingAgentPhone', e)}
              type="tel"
              value={formData.listingAgentPhone}
              className="form-control form-control-sm"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label>
              Hard Copy<span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex">
              <div className="d-flex">
                <FormControl
                  type="radio"
                  size="sm"
                  id="hardCopy"
                  onChange={() => onFormDataChange('hardCopy', { target: { value: 'yes' } })}
                  checked={formData.hardCopy === 'yes'}
                />
                <label style={{ fontSize: 14 }} className="ml-2 my-auto">
                  Yes
                </label>
              </div>
              <div className="d-flex ml-2">
                <FormControl
                  type="radio"
                  style={{
                    height: 22,
                    width: 22
                  }}
                  onChange={() => onFormDataChange('hardCopy', { target: { value: 'no' } })}
                  checked={formData.hardCopy === 'no'}
                />
                <label style={{ fontSize: 14 }} className="ml-2 my-auto">
                  No
                </label>
              </div>
            </div>
            {isFieldNotValid('hardCopy') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label>
              Title Certification<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="titleCertification"
              onChange={e => onFormDataChange('titleCertification', e)}
              type="text"
              value={formData.titleCertification}
              className={`form-control form-control-sm ${isFieldNotValid('titleCertification') && 'input-invalid'}`}
            />
            {isFieldNotValid('titleCertification') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label>
              Buyer Certification<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="buyerCertification"
              onChange={e => onFormDataChange('buyerCertification', e)}
              type="text"
              value={formData.buyerCertification}
              className={`form-control form-control-sm ${isFieldNotValid('buyerCertification') && 'input-invalid'}`}
            />
            {isFieldNotValid('buyerCertification') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label>
              Lender Certification<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="lenderCertification"
              onChange={e => onFormDataChange('lenderCertification', e)}
              type="text"
              value={formData.lenderCertification}
              className={`form-control form-control-sm ${isFieldNotValid('lenderCertification') && 'input-invalid'}`}
            />
            {isFieldNotValid('lenderCertification') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Form.Group className="midFont">
            <Form.Label>
              Underwriter Certification<span className="text-danger">*</span>
            </Form.Label>
            <select
              id="underwriterCertification"
              onChange={e => handleUnderwriterCertification('value', e)}
              className={`form-control form-control-sm ${isFieldNotValid('underwriterCertification') &&
                formData.underwriterCertification.value !== 'Others' &&
                'input-invalid'}`}
              value={formData.underwriterCertification.value}
            >
              {underwriterCertification.map((order, index) => (
                <option key={index} value={order}>
                  {order}
                </option>
              ))}
            </select>
          </Form.Group>
          {isFieldNotValid('underwriterCertification') && formData.underwriterCertification.value !== 'Others' && (
            <p className="xsmallFont text-rush">This is a required Field</p>
          )}
        </Col>
        {formData.underwriterCertification.value === 'Others' && (
          <Col lg={12}>
            <Form.Group className="midFont">
              <input
                id="underwriterCertification"
                onChange={e => handleUnderwriterCertification('other', e)}
                type="text"
                value={formData.underwriterCertification.other}
                className={`form-control form-control-sm ${isFieldNotValid('underwriterCertification') &&
                  'input-invalid'}`}
                placeholder="Other Underwriter Certification"
              />
              {isFieldNotValid('underwriterCertification') && (
                <p className="xsmallFont text-rush">This is a required Field</p>
              )}
            </Form.Group>
          </Col>
        )}
      </Row>

      {!isEditingOrder?._id && (
        <Row className="mt-4">
          <Col xs={12} md={6} lg={4} className="pl-md-3">
            <AttachSupportingDocuments
              variant="compact"
              documents={supportingDocuments}
              onDocumentsChange={setSupportingDocuments}
              disabled={loading}
              folderId={folderId}
              orderType="Survey"
            />
          </Col>
        </Row>
      )}
      <div className="d-flex justify-content-end align-items-end sticky-bottom my-3">
        <Button
          variant="success"
          className="d-inline-flex align-items-center justify-content-center"
          disabled={
            loading ||
            hasSupportingDocumentUploadIssues ||
            uploadedFiles.some(
              f => f.status === 'WAITING_FOR_UPLOAD' || f.status === 'UPLOADING' || f.status === 'ERROR'
            )
          }
          onClick={onHandleClickSubmit}
        >
          {loading && <Spinner size="sm" animation="border" className="mr-1" />}
          {!isEditingOrder?._id ? 'Add' : 'Update'} Survey
        </Button>
      </div>
    </Container>
  );
};

export default SurveyOrderForm;
